/**
 * MapCanvasPanel — 시뮬레이터 페이지에 등록된 맵을 임베드한다.
 *
 * 뷰어(ViewerPage)와 동일한 부트스트랩 경로를 재사용한다:
 *   useProjectInitializer(mapId) 로 floorStore 를 하이드레이트 →
 *   해당 lot 의 floors 를 MultiFloorCanvas 로 렌더 → StatusOverlay 가 따라붙음.
 *
 * P1a 범위: 맵을 "보여주기"만 한다. 상태 피드(useStatusStore.connect)는 연결하지
 * 않으므로 깨끗한 도면이 그려진다. 실시간 주차 점유/CCTV 색칠은 후속 단계에서
 * 실데이터 피드를 연결하며 추가된다.
 */
import { useEffect, useMemo, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { ThemeProvider } from '../../map-editor-shared/context/ThemeContext';
import { useProjectInitializer } from '../../map-editor-shared/hooks/useProjectInitializer';
import { useFloorStore } from '../../map-editor-shared/store/floorStore';
import type { Floor } from '../../map-editor-shared/store/floorStore';
import { CanvasFloorViewer } from '../../map-editor-features/viewer';
import { LoadingOverlay } from '../../map-editor-shared/ui/LoadingOverlay';
import type { SimulatorEmergencyBell } from '../../models/Simulator';
import { useSimInteractionStore } from './model/simInteractionStore';
import {
  fixDeviceIcons,
  normalizeLightingFrames,
  addCctvNameLabels,
  addBellNameLabels,
  keepOnlyCctvAndZoneLabels,
  extractCctvNames,
  extractCctvList,
  extractCctvParkingLinks,
  extractCctvLightLinks,
  extractMapBells,
  type CctvParkingLink,
  type CctvLightLink,
  type MapCctv,
  type MapBell,
} from './mapAssetFix';
import styles from './MapCanvasPanel.module.css';

/** 이름 라벨 표시 여부 영속화(localStorage). CCTV 이름 기본 ON, 주차면 이름 기본 OFF. */
const SHOW_CCTV_NAMES_KEY = 'simulator-map-show-cctv-names';
const SHOW_PARKING_NAMES_KEY = 'simulator-map-show-parking-names';
const SHOW_CCTV_LINKS_KEY = 'simulator-map-show-cctv-links';
function loadFlag(key: string, defaultOn: boolean): boolean {
  try {
    const v = localStorage.getItem(key);
    return v === null ? defaultOn : v === '1';
  } catch {
    return defaultOn;
  }
}
function saveFlag(key: string, value: boolean): void {
  try {
    localStorage.setItem(key, value ? '1' : '0');
  } catch {
    /* private 모드 등에서 실패해도 토글 동작 자체는 유지 */
  }
}

/** opt-in: 비상벨 맵 클릭 설정. 전달 시 맵 위 비상벨 마커 클릭이 활성화된다. */
interface BellClickProp {
  bells: SimulatorEmergencyBell[];
}

interface MapCanvasPanelProps {
  /** 선택된 맵 id (= map_projects.id, map-data API 의 조회 id). 미선택 시 null. */
  mapId: number | null;
  /** 캔버스 영역 높이 (기본 520px). */
  height?: number | string;
  /** opt-in: 비상벨 맵 클릭 레이어 (시뮬레이터 비상벨 탭에서만 전달). */
  bellClick?: BellClickProp;
  /** 맵에 실제 배치된 CCTV 이름 목록을 상위로 보고(비상벨/만공차 탭 선택지에 사용). */
  onCctvNames?: (names: string[]) => void;
  /** 맵 CCTV 를 이름+IP 로 보고(비상벨 탭 "이름 (IP)" 다중 선택지에 사용). */
  onCctvList?: (list: MapCctv[]) => void;
  /** CCTV↔주차면 1:n 관계를 상위로 보고(만공차 체크 탭에서 사용). */
  onCctvParkingLinks?: (links: CctvParkingLink[]) => void;
  /** CCTV↔조명 관계를 상위로 보고(비상벨 탭 조명 점멸에 사용). */
  onCctvLightLinks?: (links: CctvLightLink[]) => void;
  /** 맵 graphJson 에서 추출한 비상벨 목록(API 폴백)을 상위로 보고. */
  onMapBells?: (bells: MapBell[]) => void;
}

/** graphJson 에 배치된 객체(CCTV/주차면 등)가 있는, 렌더 가능한 floor 인지. */
function hasRenderableData(floor: Floor): boolean {
  const cells = floor.mapData?.graphJson?.cells;
  return Array.isArray(cells) && cells.length > 0;
}

/** ThemeProvider 안에서 동작하는 내부 본체 — useProjectInitializer 가 mapId 별 데이터를 보장. */
function MapCanvasBody({
  mapId,
  height,
  bellClick,
  onCctvNames,
  onCctvList,
  onCctvParkingLinks,
  onCctvLightLinks,
  onMapBells,
}: {
  mapId: number;
  height: number | string;
  bellClick?: BellClickProp;
  onCctvNames?: (names: string[]) => void;
  onCctvList?: (list: MapCctv[]) => void;
  onCctvParkingLinks?: (links: CctvParkingLink[]) => void;
  onCctvLightLinks?: (links: CctvLightLink[]) => void;
  onMapBells?: (bells: MapBell[]) => void;
}) {
  const projectId = String(mapId);
  const { loading, error } = useProjectInitializer(projectId, projectId);
  const floors = useFloorStore((state) => state.floors);
  // 이름 라벨 표시 토글(카테고리별) — display 토글이라 줌/팬 보존.
  const [showCctvNames, setShowCctvNames] = useState<boolean>(() =>
    loadFlag(SHOW_CCTV_NAMES_KEY, true),
  );
  const [showParkingNames, setShowParkingNames] = useState<boolean>(() =>
    loadFlag(SHOW_PARKING_NAMES_KEY, false),
  );
  const [showCctvLinks, setShowCctvLinks] = useState<boolean>(() =>
    loadFlag(SHOW_CCTV_LINKS_KEY, true),
  );

  const toggleCctvNames = () =>
    setShowCctvNames(prev => {
      const next = !prev;
      saveFlag(SHOW_CCTV_NAMES_KEY, next);
      return next;
    });
  const toggleParkingNames = () =>
    setShowParkingNames(prev => {
      const next = !prev;
      saveFlag(SHOW_PARKING_NAMES_KEY, next);
      return next;
    });
  const toggleCctvLinks = () =>
    setShowCctvLinks(prev => {
      const next = !prev;
      saveFlag(SHOW_CCTV_LINKS_KEY, next);
      return next;
    });

  const lotFloors = useMemo(() => {
    return floors
      .filter((f) => String(f.lotId) === projectId && hasRenderableData(f))
      .sort((a, b) => a.order - b.order)
      // 주차장 템플릿 기준 보정: 디바이스 아이콘 엑박 교체 + 조명 프레임 stroke-only
      // + 맵 위 라벨을 CCTV·주차구역·비상벨 이름 3종류로만 제한(흰색 강제).
      .map((f) => ({
        ...f,
        mapData: {
          ...f.mapData,
          graphJson: keepOnlyCctvAndZoneLabels(
            addBellNameLabels(
              addCctvNameLabels(
                normalizeLightingFrames(fixDeviceIcons(f.mapData?.graphJson)),
              ),
            ),
          ),
        },
      }));
  }, [floors, projectId]);

  // 맵에 실제 배치된 CCTV 이름(중복 제거) — 비상벨 탭 선택지 등에 쓰도록 상위로 보고.
  const mapCctvNames = useMemo(() => {
    const all = new Set<string>();
    for (const f of lotFloors) {
      for (const n of extractCctvNames(f.mapData?.graphJson)) all.add(n);
    }
    return Array.from(all);
  }, [lotFloors]);

  useEffect(() => {
    onCctvNames?.(mapCctvNames);
  }, [mapCctvNames, onCctvNames]);

  // 맵 CCTV 이름+IP — 비상벨 탭 "이름 (IP)" 다중 선택지에 사용.
  const mapCctvList = useMemo(() => {
    const seen = new Set<string>();
    const out: MapCctv[] = [];
    for (const f of lotFloors) {
      for (const c of extractCctvList(f.mapData?.graphJson)) {
        if (!seen.has(c.name)) {
          seen.add(c.name);
          out.push(c);
        }
      }
    }
    return out;
  }, [lotFloors]);

  useEffect(() => {
    onCctvList?.(mapCctvList);
  }, [mapCctvList, onCctvList]);

  // CCTV↔주차면 1:n 관계(만공차 체크 탭에서 점유→만공차등 계산용).
  const cctvParkingLinks = useMemo(() => {
    const merged: CctvParkingLink[] = [];
    for (const f of lotFloors) merged.push(...extractCctvParkingLinks(f.mapData?.graphJson));
    return merged;
  }, [lotFloors]);

  useEffect(() => {
    onCctvParkingLinks?.(cctvParkingLinks);
  }, [cctvParkingLinks, onCctvParkingLinks]);

  // CCTV↔조명 관계(비상벨 호출 시 연결 조명 점멸용). 저장된 관계가 없으면
  // 최근접 CCTV 파티셔닝 폴백(extractCctvLightLinks 참고).
  const cctvLightLinks = useMemo(() => {
    const merged: CctvLightLink[] = [];
    for (const f of lotFloors) merged.push(...extractCctvLightLinks(f.mapData?.graphJson));
    return merged;
  }, [lotFloors]);

  useEffect(() => {
    onCctvLightLinks?.(cctvLightLinks);
  }, [cctvLightLinks, onCctvLightLinks]);

  // 맵 비상벨(API 폴백) — 벨 이름/좌표 + 연결 CCTV(관계 우선, 최근접 폴백).
  const mapBells = useMemo(() => {
    const merged: MapBell[] = [];
    const seen = new Set<string>();
    for (const f of lotFloors) {
      for (const b of extractMapBells(f.mapData?.graphJson)) {
        if (!seen.has(b.id)) {
          seen.add(b.id);
          merged.push(b);
        }
      }
    }
    return merged;
  }, [lotFloors]);

  useEffect(() => {
    onMapBells?.(mapBells);
  }, [mapBells, onMapBells]);

  // 점유된 주차면(맵 빨간색) — 만공차 체크 탭과 공유하는 단일 소스.
  const occupiedParkingIds = useSimInteractionStore(s => s.occupiedParkingIds);

  // 맵 포커스(탭 선택/맵 클릭) → 층별 셀 해석. 비상벨은 cell id, CCTV 는 이름으로
  // 찾아 해당 층 뷰어에 선택 테두리(selectedIds) + 펄스 링/센터링(focusPoint)을 준다.
  const mapFocus = useSimInteractionStore(s => s.mapFocus);
  const focusByFloor = useMemo(() => {
    const out = new Map<string, { ids: Set<string>; point: { x: number; y: number; nonce: number } }>();
    if (!mapFocus) return out;
    const norm = (s: string) => s.trim().toLowerCase();
    for (const f of lotFloors) {
      const cells = f.mapData?.graphJson?.cells;
      if (!Array.isArray(cells)) continue;
      const ids = new Set<string>();
      let point: { x: number; y: number } | null = null;
      for (const cell of cells) {
        const cellId = cell?.id != null ? String(cell.id) : '';
        if (!cellId) continue;
        let hit = false;
        if (mapFocus.kind === 'bell') {
          hit = cellId === mapFocus.id;
        } else if (mapFocus.name) {
          const nm = (cell as { data?: { properties?: { name?: unknown } } }).data?.properties?.name;
          hit = typeof nm === 'string' && norm(nm) === norm(mapFocus.name);
        }
        if (!hit) continue;
        ids.add(cellId);
        if (!point) {
          const px = Number((cell as { position?: { x?: unknown } }).position?.x);
          const py = Number((cell as { position?: { y?: unknown } }).position?.y);
          const w = Number((cell as { size?: { width?: unknown } }).size?.width) || 0;
          const h = Number((cell as { size?: { height?: unknown } }).size?.height) || 0;
          if (Number.isFinite(px) && Number.isFinite(py)) {
            point = { x: px + w / 2, y: py + h / 2 };
          }
        }
      }
      if (ids.size > 0 && point) {
        out.set(f.id, { ids, point: { ...point, nonce: mapFocus.nonce } });
      }
    }
    return out;
  }, [mapFocus, lotFloors]);

  if (loading) {
    return (
      <Box sx={{ height, position: 'relative' }}>
        <LoadingOverlay message="맵 로딩 중..." />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
        <Typography variant="body2" color="error">
          맵을 불러올 수 없습니다: {error}
        </Typography>
      </Box>
    );
  }

  if (lotFloors.length === 0) {
    return (
      <Box sx={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
        <Typography variant="body2" color="text.secondary">
          이 맵에는 표시할 도면 데이터가 없습니다.
        </Typography>
      </Box>
    );
  }

  // Canvas 렌더러(CanvasFloorViewer)로 렌더. 상태색·조명 bloom 은 CanvasFloorViewer 가
  // statusStore 구독으로 그린다. 비상벨 클릭은 맵 클릭 콜백에서 매칭해 트리거한다
  // (EmergencyBellClickLayer 와 동일한 id→name→linked_cctv_id 우선순위).
  const gridCols = lotFloors.length === 1 ? 1 : lotFloors.length <= 4 ? 2 : 3;

  const handleMapClick = (
    cellId: string,
    cellName: string | null,
    isCctv: boolean,
    isParking: boolean,
  ) => {
    // CCTV 클릭은 이름을 게시해 비상벨 탭의 수동 CCTV 입력을 채운다 + 맵 포커스
    // 표시(어떤 객체를 클릭했는지 선택 테두리/펄스 링).
    if (isCctv && cellName) {
      useSimInteractionStore.getState().selectCctv(cellName);
      useSimInteractionStore.getState().setMapFocus({ kind: 'cctv', name: cellName });
    }
    // 주차면 클릭은 점유를 토글한다(만공차 체크 탭과 공유 → 맵에 빨간색).
    if (isParking) {
      useSimInteractionStore.getState().toggleParkingOccupied(cellId);
    }
    // 비상벨 매칭(id→name→linked_cctv_id) → 트리거 + 맵 포커스 표시.
    const bells = bellClick?.bells;
    if (!bells || bells.length === 0) return;
    const norm = (s: string) => s.trim().toLowerCase();
    let bell = bells.find(b => String(b.id) === cellId);
    if (!bell && cellName) {
      const n = norm(cellName);
      bell =
        bells.find(b => b.name && norm(b.name) === n) ??
        bells.find(b => b.linked_cctv_id && norm(b.linked_cctv_id) === n);
    }
    if (bell) {
      useSimInteractionStore.getState().setMapFocus({ kind: 'bell', id: bell.id });
      useSimInteractionStore.getState().requestBellTrigger(bell.id, 'alert');
    }
  };

  return (
    <Box sx={{ height, position: 'relative' }}>
      <div className={[styles.canvasWrap, styles.grayscale].filter(Boolean).join(' ')}>
        <div
          style={{
            display: 'grid',
            gap: 8,
            width: '100%',
            height: '100%',
            gridTemplateColumns: `repeat(${gridCols}, 1fr)`,
            gridAutoRows: '1fr',
          }}
        >
          {lotFloors.map(floor => {
            const focus = focusByFloor.get(floor.id);
            return (
              <div key={floor.id} style={{ position: 'relative', minWidth: 0, minHeight: 0 }}>
                <CanvasFloorViewer
                  floor={floor}
                  onMapClick={handleMapClick}
                  occupiedParkingIds={occupiedParkingIds}
                  selectedIds={focus?.ids}
                  focusPoint={focus?.point ?? null}
                  showCctvNames={showCctvNames}
                  showParkingNames={showParkingNames}
                  showCctvLinks={showCctvLinks}
                />
              </div>
            );
          })}
        </div>
      </div>
      <div className={styles.toggleGroup} role="group" aria-label="맵 라벨 표시 토글">
        <span className={styles.heading}>이름 표시</span>
        <label className={styles.toggleLabel} title="CCTV 이름 표시">
          <input
            type="checkbox"
            className={styles.checkbox}
            checked={showCctvNames}
            onChange={toggleCctvNames}
          />
          <span>CCTV</span>
        </label>
        <label className={styles.toggleLabel} title="주차면 이름 표시">
          <input
            type="checkbox"
            className={styles.checkbox}
            checked={showParkingNames}
            onChange={toggleParkingNames}
          />
          <span>주차면</span>
        </label>
        <label className={styles.toggleLabel} title="CCTV–주차면 연결선 표시">
          <input
            type="checkbox"
            className={styles.checkbox}
            checked={showCctvLinks}
            onChange={toggleCctvLinks}
          />
          <span>연결선</span>
        </label>
      </div>
    </Box>
  );
}

export default function MapCanvasPanel({ mapId, height = 520, bellClick, onCctvNames, onCctvList, onCctvParkingLinks, onCctvLightLinks, onMapBells }: MapCanvasPanelProps) {
  if (mapId === null) {
    return (
      <Box sx={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
        <Typography variant="body2" color="text.secondary">
          맵을 선택하면 도면이 표시됩니다.
        </Typography>
      </Box>
    );
  }

  return (
    <ThemeProvider>
      {/* mapId 가 바뀌면 내부 상태를 새로 초기화하도록 key 로 강제 리마운트. */}
      <MapCanvasBody key={mapId} mapId={mapId} height={height} bellClick={bellClick} onCctvNames={onCctvNames} onCctvList={onCctvList} onCctvParkingLinks={onCctvParkingLinks} onCctvLightLinks={onCctvLightLinks} onMapBells={onMapBells} />
    </ThemeProvider>
  );
}
